import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, OutlinedInput, Stack, Typography } from '@mui/material';

import dayjs from 'dayjs';

import { debounce, filter } from 'lodash';
import { MRT_SortingState, MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { ShowFnOutput, useModal } from 'mui-modal-provider';

import { venueListReservationsApi } from '../../../api/reservations/venueListReservationsApi';
import { enableShopifyVenueID } from '../../../config';
import { useNotificationsContext } from '../../../contexts/NotificationsContext';
import { useReservationLimitInPeriodContext } from '../../../contexts/ReservationsLimitInPeriodContext';
import {
  Reservation,
  ReservationReviewType,
  ReservationStatus,
  ShippingStatus,
  VenueListReservationsFilter,
} from '../../../graphql/API';
import { useVenueListReservationsCountQuery } from '../../../hooks/reactQuery/reservations/useVenueListReservationsCountQuery';
import useReservationQuery from '../../../hooks/reactQuery/useReservationQuery';
import useExportCsv from '../../../hooks/useExportCsv';
import useVenueId from '../../../hooks/useVenueId';
import { extractSubscriptionPlan } from '../../../pages/SubscriptionPlans/common/helper';
import { useAppSelector } from '../../../redux/hooks';
import Icons from '../../../themes/icons';
import { brandPromotionLimit } from '../../../utils/reservations';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import { BrandPromotionLimit } from '../../BrandPromotionLimit/BrandPromotionLimit';
import LoadingButton from '../../Button/LoadingButton';
import ChipOptions from '../../ChipOptions/ChipOptions';
import { AddTrackingNumberDialog } from '../../Dialogs/AddTrackingNumber/AddTrackingNumberDialog';
import { BaseDialogProps } from '../../Dialogs/Base';
import { HtmlTooltip } from '../../HtmlTooltip/HtmlTooltip';
import { OptionsBar } from '../../OptionsBar/OptionsBar';
import useColumn from './Column';
import mapToCsv, { mapToCsvForOnlineBrand } from './mapToCsv';
import './resrvation.css';

export enum ExtendedReservationStatus {
  completed = 'completed',
  delivered = 'delivered',
}

enum ReservationPromotionStatus {
  completed = 'completed',
  notCompleted = 'not-completed',
}

type ReservationsTableProps = {
  height?: string;
  detail?: boolean;
  filters?: Partial<VenueListReservationsFilter>;
  dateRange?: { start: string; end: string };
  onClearDateRange?: () => void;
  handleTableHeight?: (val: number) => void;
  showPromotionLimitPopup?: boolean;
};

const ReservationsTable: React.FC<ReservationsTableProps> = ({
  height,
  detail,
  filters: defaultFilters,
  dateRange,
  onClearDateRange,
  handleTableHeight,
  showPromotionLimitPopup = true,
}) => {
  const venueID = useVenueId();
  const navigate = useNavigate();
  const { showModal } = useModal();

  const shipmentReservationModalRef = React.useRef<ShowFnOutput<BaseDialogProps>>();

  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);
  const [exportLoading, setExportLoading] = React.useState<boolean>();
  const [filters, setFilters] = React.useState<Partial<VenueListReservationsFilter> | undefined>(
    defaultFilters,
  );
  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);
  const [sorting, setSorting] = React.useState<MRT_SortingState>([
    { id: defaultFilters?.sortBy ?? 'createdAt', desc: defaultFilters?.sortDirection === 'desc' },
  ]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch: refetchReservationsList,
  } = useReservationQuery(filters, venueID, 50);

  const displayedData = React.useMemo(() => {
    if (!dateRange) return data;
    const startMs = new Date(dateRange.start).getTime();
    const endMs = new Date(dateRange.end).getTime();
    return data.filter((r) => {
      const created = r.createdAt ? new Date(r.createdAt).getTime() : NaN;
      return Number.isFinite(created) && created >= startMs && created < endMs;
    });
  }, [data, dateRange]);

  const reachedPeriodStart = React.useMemo(() => {
    if (!dateRange || data.length === 0) return false;
    const oldest = data[data.length - 1]?.createdAt;
    if (!oldest) return false;
    return new Date(oldest).getTime() < new Date(dateRange.start).getTime();
  }, [data, dateRange]);

  const { totalCompletedPromotions, totalReservedAndCheckedInReservations } =
    useReservationLimitInPeriodContext() || {};

  const { data: allReservationsCount, refetch: refetchReservationsCountHandle } =
    useVenueListReservationsCountQuery({
      venueID: venueID || '',
      search: filters?.searchText,
      offerID: filters?.offerID,
      shippingStatus: filters?.shippingStatus,
      subscriptionDates: dateRange
        ? { start: dateRange.start, end: dateRange.end }
        : undefined,
    });

  const { handleUpdateReservation } = useNotificationsContext() || {};

  const { doExportData } = useExportCsv();

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !isFetching &&
          hasNextPage &&
          !reachedPeriodStart
        ) {
          fetchNextPage();
        }
      }
    },
    [isFetching, fetchNextPage, hasNextPage, reachedPeriodStart],
  );

  // When no fixed height is provided the table grows with its content and the
  // page itself scrolls, so infinite loading must react to the scrolling
  // ancestor (captured on window) instead of the table's own container.
  const usePageScroll = !height;

  React.useEffect(() => {
    if (!usePageScroll) return;

    const onScroll = (event: Event) => {
      fetchMoreOnBottomReached(event.target as HTMLDivElement);
    };

    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [usePageScroll, fetchMoreOnBottomReached]);

  React.useEffect(() => {
    if (data.length > 0 && handleUpdateReservation) {
      handleUpdateReservation(data[0].createdAt || '');
    }
  }, [data]);

  React.useEffect(() => {
    const [sort] = sorting;
    setFilters((x) => ({ ...x, sortBy: sort?.id, sortDirection: sort?.desc ? 'desc' : 'asc' }));
  }, [sorting]);

  const fetchReservationsInChunks = async ({
    filters,
  }: {
    filters: Partial<VenueListReservationsFilter>;
  }) => {
    const chunkSize = 100;
    let offset = 0;
    let allItems: Reservation[] = [];
    let hasMoreData = true;

    while (hasMoreData) {
      const items = await venueListReservationsApi({
        ...filters,
        limit: chunkSize,
        offset,
        venueID,
      });

      allItems = allItems.concat(items);
      hasMoreData = items.length === chunkSize;
      offset += chunkSize;
    }

    return allItems;
  };

  const handleExportData = React.useCallback(async () => {
    setExportLoading(true);
    const items = await fetchReservationsInChunks({ filters: filters || {} });

    if (isOnlineBrand) {
      doExportData(items.map(mapToCsvForOnlineBrand));
    } else {
      doExportData(items.map(mapToCsv));
    }
    setExportLoading(false);
  }, [doExportData, venueID, filters, isOnlineBrand]);

  const onMarkShipmentConfirmDialogHandle = (reservation: Reservation) => {
    shipmentReservationModalRef.current = showModal(AddTrackingNumberDialog, {
      modalRef: shipmentReservationModalRef,
      reservation,
      refetchReservationDetails: () => {
        refetchReservationsList();
        refetchReservationsCountHandle();
      },
    });
  };

  const columns = useColumn({
    detail,
    isOnlineBrand,
    onAddTracking: onMarkShipmentConfirmDialogHandle,
    onMarkAsShipped: onMarkShipmentConfirmDialogHandle,
  });

  const table = useMaterialReactTable({
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: true,
    manualSorting: true,
    onSortingChange: setSorting,
    muiTableHeadProps: { sx: { opacity: 1 } },
    muiTableHeadRowProps: {
      sx: { backgroundColor: 'white', boxShadow: 'none' },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: { backgroundColor: 'inherit', cursor: 'pointer' },
      onClick: () => {
        navigate(`/reservations/${row.original.id}`);
      },
    }),
    muiTableBodyCellProps: {
      sx: ({ custom }) => ({ borderColor: custom.colors.brandLightGrey }),
    },
    muiTableHeadCellProps: {
      sx: ({ custom }) => ({
        color: 'text.secondary',
        borderColor: custom.colors.brandLightGrey,
        fontWeight: isOnlineBrand ? 400 : 500,
      }),
    },
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
    muiTableContainerProps: {
      sx: { height, maxHeight: height, backgroundColor: 'white' },
      onScroll: (event) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    columns,
    data: displayedData,
    rowVirtualizerOptions: { overscan: 5 },
    state: { sorting, isLoading: isLoading || isFetching },
    renderEmptyRowsFallback: () => (
      <Typography
        textAlign='center'
        sx={{ color: 'rgba(0, 0, 0, 0.6)', py: '2rem', fontStyle: 'italic' }}
      >
        You don't have any reservations
      </Typography>
    ),
  });

  const handleSearch = debounce((value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchText: value,
    }));
  }, 500);

  const handleChange = React.useCallback(
    (value?: string) => {
      handleSearch(value);
    },
    [handleSearch],
  );

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const totalReservations = React.useMemo(
    () => (isOnlineBrand ? totalCompletedPromotions : totalReservedAndCheckedInReservations) || 0,
    [isOnlineBrand, totalCompletedPromotions, totalReservedAndCheckedInReservations],
  );

  const isPromotionLimitHit = React.useMemo(
    () =>
      venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) &&
      (subscriptionPlan === 'basic' ||
        subscriptionPlan === 'starter' ||
        subscriptionPlan === 'plus'),
    [subscriptionPlan, venueState?.tags],
  );

  React.useEffect(() => {
    if (isPromotionLimitHit && handleTableHeight) {
      handleTableHeight(400);
    } else if (handleTableHeight) {
      handleTableHeight(298);
    }
  }, [isPromotionLimitHit]);

  const totalAllReservations =
    (allReservationsCount?.totalReserved || 0) +
    (allReservationsCount?.totalCheckedIn || 0) +
    (allReservationsCount?.totalCancelled || 0) +
    (allReservationsCount?.totalNoShow || 0) +
    (allReservationsCount?.totalNoShowProcessed || 0);

  const venueReservationStatuses = [
    { label: `All (${totalAllReservations})`, value: '' },
    {
      label: `Reserved (${allReservationsCount?.totalReserved || 0})`,
      value: ReservationStatus.reserved,
    },
    {
      label: `Checked in (${
        allReservationsCount?.totalCheckedInWithoutRequirementSubmission || 0
      })`,
      value: ReservationStatus.checkedin,
    },
    {
      label: `Completed (${allReservationsCount?.totalCheckedInWithRequirementSubmission || 0})`,
      value: ExtendedReservationStatus.completed,
    },
    {
      label: `Cancelled (${allReservationsCount?.totalCancelled || 0})`,
      value: ReservationStatus.cancelled,
    },
    {
      label: `No show (${
        (allReservationsCount?.totalNoShow || 0) + (allReservationsCount?.totalNoShowProcessed || 0)
      })`,
      value: ReservationStatus.noshow,
    },
  ];

  const productReservationStatuses = [
    { label: `All`, value: '' },
    {
      label: `Waiting for shipment (${
        allReservationsCount?.totalWatingShippingWithoutAdditionalCheck || 0
      })`,
      value: ShippingStatus.waiting,
    },
    {
      label: `In Transit (${allReservationsCount?.totalInTransitReservations || 0})`,
      value: ShippingStatus.intransit,
    },
    {
      label: `Delivered (${allReservationsCount?.totalDeliveredReservations || 0})`,
      value: ShippingStatus.delivered,
    },
    {
      label: `Cancelled (${allReservationsCount?.totalCancelled || 0})`,
      value: ShippingStatus.cancelled,
    },
  ];

  const productReservationPromotionStatuses = [
    { label: `All`, value: '' },
    {
      label: `Completed (${allReservationsCount?.totalCompletedPromotions || 0})`,
      value: ReservationPromotionStatus.completed,
    },
    {
      label: `Not completed (${
        allReservationsCount?.totalNotCompletedPromotions14DaysPassed || 0
      })`,
      value: ReservationPromotionStatus.notCompleted,
    },
  ];

  return (
    <Stack spacing={4}>
      {/* {enableShopifyVenueID && String(venueID) === String(enableShopifyVenueID) ? null : (
        <>
          {isPromotionLimitHit && showPromotionLimitPopup && (
            <Box sx={{ py: 0.5 }}>
              <BrandPromotionLimit
                title='Monthly promotions'
                totalReservations={totalReservations}
                outOfReservations={brandPromotionLimit[subscriptionPlan] || 0}
                subscription={subscription}
                subscriptionPlan={subscriptionPlan}
                showFullWidth={
                  totalReservations === 0
                    ? false
                    : totalReservations >= (brandPromotionLimit[subscriptionPlan] || 0)
                }
                toolTipComponent={
                  <HtmlTooltip
                    arrow
                    placement='top'
                    title={
                      <React.Fragment>
                        <Typography fontSize={'15px'} p={'12px'}>
                          These are promotion status completed. Reservation that are waiting for
                          shipment or in transit are not counted yet
                        </Typography>
                      </React.Fragment>
                    }
                  >
                    <Icons.InfoLightIcon style={{ width: '18px', height: '18px' }} />
                  </HtmlTooltip>
                }
              />
            </Box>
          )}
        </>
      )} */}

      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={isOnlineBrand ? 'flex-end' : 'center'}
      >
        {isOnlineBrand ? (
          <Box display={'flex'} alignItems={'center'} gap={'16px'}>
            <Box display={'flex'} flexDirection={'column'} gap='8px'>
              <Typography fontSize={'13px'} fontWeight={500}>
                Shipping status
              </Typography>
              <OptionsBar
                styles={{ fontSize: '14px' }}
                value={filters?.shippingStatus || ''}
                options={productReservationStatuses}
                onChange={(v) => {
                  setFilters((pre) => ({
                    ...pre,
                    status: [],
                    shippingStatus: !v ? undefined : (v as ShippingStatus),
                  }));
                }}
              />
            </Box>
            <Box display={'flex'} flexDirection={'column'} gap='8px'>
              <Typography fontSize={'13px'} fontWeight={500}>
                Promotion status
              </Typography>
              <OptionsBar
                styles={{ fontSize: '14px' }}
                value={
                  filters?.requirementsSubmitted === true
                    ? ReservationPromotionStatus.completed
                    : filters?.isPast14Days === true
                      ? ReservationPromotionStatus.notCompleted
                      : ''
                }
                options={productReservationPromotionStatuses}
                onChange={(v) => {
                  setFilters((pre) => ({
                    ...pre,
                    requirementsSubmitted:
                      v === ReservationPromotionStatus.completed
                        ? true
                        : v === ReservationPromotionStatus.notCompleted
                          ? false
                          : undefined,
                    isPast14Days: v === ReservationPromotionStatus.notCompleted ? true : undefined,
                    excludeShippingStatuses:
                      v === ReservationPromotionStatus.notCompleted
                        ? [ShippingStatus.cancelled, ShippingStatus.waiting]
                        : undefined,
                  }));
                }}
              />
            </Box>
          </Box>
        ) : (
          <ChipOptions
            name='reservationStatus'
            options={venueReservationStatuses}
            onChange={({ value: v }) => {
              setFilters((pre) => ({
                ...pre,
                status: v
                  ? v === ReservationStatus.noshow
                    ? [ReservationStatus.noshow, ReservationStatus.noshowprocessed]
                    : v === ExtendedReservationStatus.completed
                      ? [ReservationStatus.checkedin]
                      : [v as ReservationStatus]
                  : undefined,
                requirementsSubmitted:
                  v === ExtendedReservationStatus.completed
                    ? true
                    : v === ReservationStatus.checkedin
                      ? false
                      : undefined,
                validationIsNull: v === ReservationStatus.checkedin ? true : undefined,
              }));
            }}
          />
        )}

        <Stack direction={isOnlineBrand ? 'row-reverse' : 'row'} spacing={1.5}>
          <OutlinedInput
            id='outlined-adornment-amount'
            placeholder='Search a member...'
            startAdornment={
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            }
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            size='small'
            sx={{ minWidth: '228px' }}
          />
          <LoadingButton
            variant={isOnlineBrand ? 'contained' : 'outlined'}
            onClick={handleExportData}
            startIcon={isOnlineBrand ? <Icons.AddressDownloadIcon /> : <FileDownloadIcon />}
            loading={exportLoading}
            disabled={data.length <= 0}
            sx={
              isOnlineBrand
                ? {
                    px: 2.2,
                    py: 0.8,
                    backgroundColor: '#f1f1f1',
                    color: '#1A1C1E',
                    boxShadow: 'none',
                    border: '1px solid #e5e5e5',
                    '&:hover': {
                      backgroundColor: '#e5e5e5',
                      boxShadow: 'none',
                    },
                    '& .MuiSvgIcon-root': { color: '#1A1C1E' },
                  }
                : { px: 2.2, py: 0.8 }
            }
          >
            {isOnlineBrand ? 'Export addresses' : 'Export Data'}
          </LoadingButton>
        </Stack>
      </Box>

      {dateRange && (
        <Box
          sx={{
            mt: -3,
            mb: -2,
            display: 'inline-flex',
            width: 'fit-content',
            alignItems: 'center',
            gap: 2,
            px: 2.5,
            py: 1.25,
            borderRadius: '8px',
            backgroundColor: '#fcfcfc',
            border: '1px solid #efefef',
            color: '#1A1C1E',
          }}
        >
          <FilterAltIcon sx={{ fontSize: 20, color: '#1A1C1E' }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>
            Showing only reservations from this Monthly Promotions period (
            {dayjs(dateRange.start).format('MMM D, YYYY')} –{' '}
            {dayjs(dateRange.end).format('MMM D, YYYY')})
          </Typography>
          {onClearDateRange && (
            <Box
              component='button'
              type='button'
              onClick={onClearDateRange}
              sx={{
                appearance: 'none',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: '#1A1C1E',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'inherit',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                p: 0,
                '&:hover': { color: '#000' },
              }}
            >
              Clear filter
            </Box>
          )}
        </Box>
      )}

      <MaterialReactTable table={table} />
    </Stack>
  );
};

export default ReservationsTable;
